import { Clear, Error as ErrorIcon } from '@procore/core-icons'
import { useId } from '@react-aria/utils'
import React from 'react'
import type { ButtonProps } from '../Button'
import { Tooltip } from '../Tooltip'
import { useI18nContext } from '../_hooks/I18n'
import { addSubcomponents } from '../_utils/addSubcomponents'
import type { Props } from '../_utils/types'
import {
  StyledErrorButton,
  StyledErrorIcon,
  StyledLabel,
  StyledRemove,
  StyledToken,
} from './Token.styles'
import type { TokenContextApi, TokenProps } from './Token.types'

const TokenContext = React.createContext<TokenContextApi>({
  labelId: '',
  removeId: '',
  isDisabled: false,
})

const Parent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & TokenProps
>(function Token(
  {
    children,
    disabled = false,
    focused = false,
    error = false,
    variant,
    ...props
  },
  ref
) {
  const i18n = useI18nContext()
  const labelId = useId()
  const removeId = useId()
  const isDisabled = disabled || variant === 'disabled'
  const isFocused = focused || variant === 'focused'
  const hasError = Boolean(error)
  const errorMessage = typeof error === 'string' ? error : undefined

  return (
    <TokenContext.Provider value={{ labelId, removeId, isDisabled }}>
      <StyledToken
        ref={ref}
        tabIndex={-1}
        {...props}
        $disabled={isDisabled}
        $focused={isFocused}
        $error={hasError}
      >
        {hasError &&
          (errorMessage ? (
            <Tooltip overlay={errorMessage}>
              <StyledErrorButton
                aria-label={i18n.t('core.token.error')}
                type="button"
              >
                <ErrorIcon size="sm" />
              </StyledErrorButton>
            </Tooltip>
          ) : (
            <StyledErrorIcon aria-label={i18n.t('core.token.error')} role="img">
              <ErrorIcon size="sm" />
            </StyledErrorIcon>
          ))}
        {children}
      </StyledToken>
    </TokenContext.Provider>
  )
})

const Remove = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function TokenRemove(props, ref) {
    const i18n = useI18nContext()
    const { labelId, removeId, isDisabled } = React.useContext(TokenContext)

    return (
      <StyledRemove
        ref={ref}
        aria-hidden="false"
        aria-label={i18n.t('core.token.remove')}
        aria-labelledby={`${removeId} ${labelId}`}
        id={removeId}
        disabled={isDisabled}
        {...props}
        icon={<Clear role="img" />}
        size="sm"
        variant="tertiary"
      />
    )
  }
)

const Label = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & Props
>(function Label({ children, ...props }, ref) {
  const { labelId } = React.useContext(TokenContext)

  return (
    <StyledLabel id={labelId} ref={ref} {...props}>
      {children}
    </StyledLabel>
  )
})

Parent.displayName = 'Token'
Remove.displayName = 'Token.Remove'
Label.displayName = 'Token.Label'

/**

 We use tokens in our multi select dropdowns and file attachers to indicate what
 has been selected or attached. Tokens in these components are removable.

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-token--demo)

 @see [Design Guidelines](https://design.procore.com/tokens)

 */
export const Token = addSubcomponents(
  {
    Label,
    Remove,
  },
  Parent
)
